import React, { useEffect, useState, useRef } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import AdminSidebar from '../components/AdminSidebar';
import '../style/common.css';

function EditPO() {
  const [poList, setPOList] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [users, setUsers] = useState([]);
  const [customClient, setCustomClient] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const clientOptions = [
    'DLF', 'Medanta Gurgaon', 'Medanta Lucknow', 'Medanta Noida', 'Medanta Patna',
    'JLL', 'Cushman & Wakefield', 'Persistant', 'CBRE',
    'Grand Thorton', 'Lenskart', 'FIS', 'Assimilate',
    'Candor G1', 'Candor G2', 'Candor Noida', 'Other'
  ];

  const refs = useRef(Array.from({ length: 15 }, () => React.createRef()));

  const fetchData = async () => {
    const poSnap = await getDocs(collection(db, 'purchase_orders'));
    const userSnap = await getDocs(collection(db, 'users'));

    const activePOs = poSnap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(po => !po.completed)
      .sort((a, b) => (a.jc_number || '').localeCompare(b.jc_number || ''));

    const userMap = userSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    setPOList(activePOs);
    setUsers(userMap);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedPO) {
      const base = parseFloat(selectedPO.po_base_amount || 0);
      const tax = parseFloat(selectedPO.tax_percent || 0);
      const total = base + (base * tax / 100);
      setSelectedPO(prev => ({ ...prev, po_total_amount: total.toFixed(2) }));

      if (selectedPO.client && !clientOptions.includes(selectedPO.client)) {
        setCustomClient(selectedPO.client);
        setSelectedPO(prev => ({ ...prev, client: 'Other' }));
      }

      // ✅ Ensure `completed` is always defined
      if (selectedPO.completed === undefined) {
        setSelectedPO(prev => ({ ...prev, completed: false }));
      }
    }
  }, [selectedPO?.po_base_amount, selectedPO?.tax_percent]);

  const handleUpdate = async () => {
    try {
      const finalClient = selectedPO.client === 'Other' ? customClient.trim() : selectedPO.client;

      const poRef = doc(db, 'purchase_orders', selectedPO.id);
      await updateDoc(poRef, {
        po_number: selectedPO.po_number,
        jc_number: selectedPO.jc_number,
        location: selectedPO.location,
        po_base_amount: parseFloat(selectedPO.po_base_amount || 0),
        tax_percent: parseFloat(selectedPO.tax_percent || 0),
        po_total_amount: parseFloat(selectedPO.po_total_amount || 0),
        po_date: selectedPO.po_date,
        deadline: selectedPO.deadline || '',
        assigned_to: selectedPO.assigned_to,
        description: selectedPO.description || '',
        client: finalClient || '',
        completed: selectedPO.completed === true || selectedPO.completed === 'completed' // ✅ FIXED
      });

      alert('PO updated successfully');
      setSelectedPO(null);
      await fetchData();
    } catch (err) {
      alert('Error updating PO');
      console.error(err);
    }
  };

  const markAsCompleted = async () => {
    try {
      await updateDoc(doc(db, 'purchase_orders', selectedPO.id), {
        completed: true,
        balance_amount: 0,
      });
      alert('PO marked as completed');
      setSelectedPO(null);
      await fetchData();
    } catch (err) {
      alert('Failed to update status');
      console.error(err);
    }
  };

  const deletePO = async () => {
    const confirmDelete = window.confirm('Are you sure you want to delete this PO?');
    if (!confirmDelete) return;
    try {
      await deleteDoc(doc(db, 'purchase_orders', selectedPO.id));
      alert('PO deleted');
      setSelectedPO(null);
      await fetchData();
    } catch (err) {
      alert('Failed to delete');
      console.error(err);
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter' && refs.current[index + 1]) {
      refs.current[index + 1].current.focus();
    }
  };

  const filteredPOs = poList.filter(po =>
    po.jc_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="dashboard-wrapper">
      <AdminSidebar />
      <div className="dashboard-content">
        <h2>Edit Purchase Orders</h2>

        {!selectedPO ? (
          <>
            <div className="form-group" style={{ maxWidth: 300 }}>
              <input
                type="text"
                placeholder="Search by JC Number"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="dashboard-stats">
              {filteredPOs.map(po => (
                <div
                  className="stat-card clickable"
                  key={po.id}
                  onClick={() => setSelectedPO({ ...po, completed: po.completed ?? false })} // ✅ Ensures `completed` is not undefined
                >
                  <h2 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>{po.po_number}</h2>
                  <p style={{ fontSize: '0.9rem', margin: 0 }}>JC Number: {po.jc_number}</p>
                  <p style={{ fontSize: '0.9rem', margin: 0 }}>
                    Assigned To: {users.find(u => u.id === po.assigned_to)?.name || 'Unassigned'}
                  </p>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="form">
            <h3>Edit PO: {selectedPO.po_number}</h3>

            {[
              { label: 'PO Number', key: 'po_number' },
              { label: 'JC Number', key: 'jc_number' },
              { label: 'Location', key: 'location' },
              { label: 'Base Amount (₹)', key: 'po_base_amount', type: 'number' },
              { label: 'GST %', key: 'tax_percent', type: 'number' },
              { label: 'PO Creation Date', key: 'po_date', type: 'date' },
              { label: 'Deadline Date', key: 'deadline', type: 'date' },
            ].map((field, idx) => (
              <div className="form-group" key={field.key}>
                <label>{field.label}</label>
                <input
                  ref={refs.current[idx]}
                  type={field.type || 'text'}
                  value={selectedPO[field.key] || ''}
                  onChange={(e) => setSelectedPO({ ...selectedPO, [field.key]: e.target.value })}
                  onKeyDown={(e) => handleKeyDown(e, idx)}
                />
              </div>
            ))}

            <div className="form-group">
              <label>Client</label>
              <select
                ref={refs.current[7]}
                value={selectedPO.client || ''}
                onChange={(e) => setSelectedPO({ ...selectedPO, client: e.target.value })}
              >
                <option value="">Select Client</option>
                {clientOptions.map((c, i) => (
                  <option key={i} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {selectedPO.client === 'Other' && (
              <div className="form-group">
                <label>Custom Client</label>
                <input
                  ref={refs.current[8]}
                  type="text"
                  value={customClient}
                  onChange={(e) => setCustomClient(e.target.value)}
                />
              </div>
            )}

            <div className="form-group">
              <label>Total Amount (₹)</label>
              <input
                type="text"
                value={selectedPO.po_total_amount || ''}
                disabled
                style={{ backgroundColor: '#eee', fontWeight: '600' }}
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={selectedPO.description || ''}
                onChange={(e) => setSelectedPO({ ...selectedPO, description: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Assign To</label>
              <select
                value={selectedPO.assigned_to || ''}
                onChange={(e) => setSelectedPO({ ...selectedPO, assigned_to: e.target.value })}
              >
                <option value="">-- Select PM --</option>
                {users.filter(u => u.role === 'pm').map(u => (
                  <option key={u.id} value={u.id}>{u.name || u.email}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                value={selectedPO.completed ? 'completed' : 'active'}
                onChange={(e) =>
                  setSelectedPO({
                    ...selectedPO,
                    completed: e.target.value === 'completed',
                  })
                }
              >
                <option value="active">Active</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            <button className="submit-btn" onClick={handleUpdate}>Update</button>
            <button className="submit-btn" onClick={markAsCompleted}>Mark as Completed</button>
            <button className="submit-btn danger" onClick={deletePO}>Delete PO</button>
            <button className="submit-btn" onClick={() => setSelectedPO(null)}>Back</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default EditPO;
